import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  BookOpen, ChevronRight, Award, Clock, 
  Star, CheckCircle, Languages, Sparkles,
  ScrollText, Globe2
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useI18n } from '../contexts/I18nContext';

const CourseCard = ({ course, navigate, t }) => {
  const lessonsProgress = course.progress?.percentage || 0;
  const lettersLearned = course.progress?.total_completed || 0;
  const totalLetters = course.modules?.find(m => m.id === 'letters' || m.id === 'consonants')?.lessons || course.total_lessons;
  const lettersMastery = totalLetters > 0 ? Math.round((lettersLearned / totalLetters) * 100) : 0;

  return (
    <div
      className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden hover:border-amber-500/50 transition-all group"
      data-testid={`course-card-${course.course_id}`}
    >
      {/* Course Header */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white mb-1">{course.name}</h2>
            <p className="text-2xl text-amber-400" style={{ direction: course.direction }}>
              {course.native_name}
            </p>
          </div>
          <img 
            src={`https://flagcdn.com/w40/${course.flag}.png`}
            alt={course.name}
            className="w-10 h-8 object-cover rounded"
          />
        </div>
        
        <p className="text-slate-400 text-sm mb-4">{course.description}</p>
        
        {/* Course Stats */}
        <div className="flex flex-wrap gap-4 text-sm mb-4">
          <div className="flex items-center gap-1 text-slate-300">
            <BookOpen className="w-4 h-4 text-amber-500" />
            <span>{course.total_lessons} lessons</span>
          </div>
          <div className="flex items-center gap-1 text-slate-300">
            <Clock className="w-4 h-4 text-blue-400" />
            <span>{course.estimated_time}</span>
          </div>
          <div className="flex items-center gap-1 text-slate-300">
            <Star className="w-4 h-4 text-yellow-400" />
            <span className="capitalize">{course.difficulty}</span>
          </div>
        </div>

        {/* Modules Preview */}
        <div className="space-y-2 mb-4">
          {course.modules.map((module, idx) => (
            <div key={idx} className="flex items-center justify-between text-sm">
              <span className="text-slate-400">{module.name}</span>
              <span className="text-slate-500">{t('alphabetCourses.lessons', { count: module.lessons })}</span>
            </div>
          ))}
        </div>

        {/* Progress Bars - Always Show */}
        <div className="space-y-3 mb-4">
          {/* Lessons Completed */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-400">{t('alphabetCourses.lessonsCompleted')}</span>
              <span className="text-amber-400">{lessonsProgress}%</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all"
                style={{ width: `${lessonsProgress}%` }}
              />
            </div>
          </div>
          
          {/* Letters Mastered */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-400">{t('alphabetCourses.lettersMastered')}</span>
              <span className="text-emerald-400">{lettersLearned}/{totalLetters}</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all"
                style={{ width: `${lettersMastery}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="px-6 pb-6">
        <button
          onClick={() => navigate(`/alphabet/${course.course_id}`)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold transition-colors group-hover:shadow-lg group-hover:shadow-amber-500/20"
          data-testid={`start-${course.course_id}`}
        >
          {lessonsProgress > 0 ? (
            <>
              <span>{t('alphabetCourses.continueCourse')}</span>
              <ChevronRight className="w-5 h-5" />
            </>
          ) : (
            <>
              <span>{t('alphabetCourses.startCourse')}</span>
              <ChevronRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const AlphabetCourses = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'biblical');

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await api.get('/alphabet/courses');
      setCourses(response.data.courses || []);
    } catch (err) {
      console.error('Failed to fetch alphabet courses:', err);
      toast.error(t("alphabetCourses.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  const biblicalCourses = courses.filter(c => c.category === 'biblical');
  const modernCourses = courses.filter(c => c.category === 'modern');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-amber-500/20 rounded-lg">
            <Languages className="w-6 h-6 text-amber-500" />
          </div>
          <h1 className="text-2xl font-bold text-white">{t('alphabetCourses.title')}</h1>
        </div>
        <p className="text-slate-400">
          {t('alphabetCourses.subtitle')}
        </p>
      </div>

      {/* Tabs for Biblical vs Modern */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-8 bg-slate-800/50 p-1 rounded-xl">
          <TabsTrigger 
            value="biblical" 
            className="flex items-center gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 rounded-lg py-3"
            data-testid="tab-biblical"
          >
            <ScrollText className="w-4 h-4" />
            <span>{t('alphabetCourses.tabBiblical')}</span>
            <span className="ml-1 px-2 py-0.5 rounded-full bg-slate-700/50 text-xs">
              {biblicalCourses.length}
            </span>
          </TabsTrigger>
          <TabsTrigger 
            value="modern" 
            className="flex items-center gap-2 data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-900 rounded-lg py-3"
            data-testid="tab-modern"
          >
            <Globe2 className="w-4 h-4" />
            <span>{t('alphabetCourses.tabModern')}</span>
            <span className="ml-1 px-2 py-0.5 rounded-full bg-slate-700/50 text-xs">
              {modernCourses.length}
            </span>
          </TabsTrigger>
        </TabsList>

        {/* Biblical Languages Tab */}
        <TabsContent value="biblical" className="mt-0">
          {/* Recommendation Banner for Biblical */}
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4 mb-8">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <h3 className="text-amber-300 font-semibold mb-1">{t('alphabetCourses.biblicalBannerTitle')}</h3>
                <p className="text-sm text-slate-300">
                  {t('alphabetCourses.biblicalBannerBody')}
                </p>
              </div>
            </div>
          </div>

          {/* Biblical Courses Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {biblicalCourses.map((course) => (
              <CourseCard key={course.course_id} course={course} navigate={navigate} t={t} />
            ))}
          </div>
        </TabsContent>

        {/* Modern Languages Tab */}
        <TabsContent value="modern" className="mt-0">
          {/* Info Banner for Modern */}
          <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-4 mb-8">
            <div className="flex items-start gap-3">
              <Globe2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
              <div>
                <h3 className="text-emerald-300 font-semibold mb-1">{t('alphabetCourses.modernBannerTitle')}</h3>
                <p className="text-sm text-slate-300">
                  {t('alphabetCourses.modernBannerBody')}
                </p>
              </div>
            </div>
          </div>

          {/* Modern Courses Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {modernCourses.map((course) => (
              <CourseCard key={course.course_id} course={course} navigate={navigate} t={t} />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AlphabetCourses;
